import { siteFiltersMapStore } from "react-migration/domains/sites/pipeline/filters/SiteFiltersStore/siteFiltersMapStore";
import { useCallback, useEffect, useRef } from "react";
import type { LayerTypeControlPageProps } from "../../../types";
import { SiteFiltersView } from "react-migration/lib/util/logSiteCardEvent";
import { Switch } from "react-migration/components/Switch";
import cn from "clsx";
import { useSnapshot } from "valtio";
import { mapStore } from "src/js/stores/map/store";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions/refreshSiteGeometryStore";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";
import { SiteFilters } from "react-migration/domains/sites/pipeline/filters/SiteFilters";
import { useControlTracking } from "../../../ControlTrackingContext";
import { logEvent } from "react-migration/lib/util/logEvent";

export function SitesControlPage({ disabled }: LayerTypeControlPageProps) {
  const isInitialRender = useRef(true);
  const { debouncedTrackControlChange } = useControlTracking();

  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  const {
    googleMap,
    settings: { showHiddenSites, showSharedSites, mapType, state, activeLayer, zoom },
  } = useSnapshot(mapStore);

  const handleShowHiddenSitesChange = useCallback(() => {
    debouncedTrackControlChange({ filterName: "showHiddenSites", filterState: !showHiddenSites });
    updateMapSettings("showHiddenSites", !showHiddenSites);
    logEvent("Toggle Site Visibility Map", {
      show: !showHiddenSites,
      mapType: mapType,
      state: state,
      layer: activeLayer,
      zoom: zoom,
    });
  }, [debouncedTrackControlChange, showHiddenSites, mapType, state, activeLayer, zoom]);

  const handleShowSharedSitesChange = useCallback(() => {
    debouncedTrackControlChange({ filterName: "showSharedSites", filterState: !showSharedSites });
    updateMapSettings("showSharedSites", !showSharedSites);
    logEvent("Toggle Shared Sites Map", {
      show: !showSharedSites,
      mapType: mapType,
      state: state,
      layer: activeLayer,
      zoom: zoom,
    });
  }, [debouncedTrackControlChange, showSharedSites, mapType, state, activeLayer, zoom]);

  // here we subscribe to any changes in the site filters store, triggering an update whenever any filters change
  // Note these values are currently reset everytime we enter the map page while the users adjust to site filters
  const siteFiltersStoreValues = useSnapshot(siteFiltersMapStore);

  useEffect(() => {
    // we only need to refetch sites if the filters change
    // this was getting triggered on first render
    if (!isInitialRender.current) {
      googleMap && refreshSiteGeometryStore(googleMap);
    }
  }, [siteFiltersStoreValues, googleMap]);

  return (
    <>
      <div className="relative flex items-center justify-between pb-4 rounded-sm">
        <label
          htmlFor="hidden-sites"
          className={cn("block! text-sm font-medium", {
            "opacity-50": disabled,
          })}
        >
          Show hidden sites
        </label>
        <Switch
          label="Show hidden sites"
          id="show-hidden-sites"
          disabled={disabled}
          onToggle={handleShowHiddenSitesChange}
          value={showHiddenSites}
        />
      </div>
      <div className="flex justify-between items-center rounded relative border-b border-neutral-200 pb-4">
        <label
          htmlFor="shared-sites"
          className={cn("block text-sm font-medium", {
            "opacity-50": disabled,
          })}
        >
          Shared with me
        </label>
        <Switch
          label="Shared with me"
          id="show-shared-sites"
          disabled={disabled}
          onToggle={handleShowSharedSitesChange}
          value={showSharedSites}
        />
      </div>
      <div className="pt-3">
        <SiteFilters eventMetadata={{ view: SiteFiltersView.MAP }} store={siteFiltersMapStore} filterLayout="column" />
      </div>
    </>
  );
}
